<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import powerDataAPI from 'dashboard/api/powerData';
import Button from 'dashboard/components-next/button/Button.vue';
import AddSourceDialog from '../../../powerData/components/AddSourceDialog.vue';

// "Adicionar à base": a pergunta que a base não soube responder vira um par de
// pergunta e resposta, já escrito, na base conectada ao agente. Com mais de uma
// base, a pessoa escolhe qual; sem nenhuma, a tela diz isso.
const props = defineProps({
  question: { type: String, required: true },
  agentSlug: { type: String, required: true },
});

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const dialogRef = ref(null);
const storeId = ref(null);
const bases = ref(null);
const message = ref('');
const loading = ref(false);

const openFor = id => {
  storeId.value = id;
  // O diálogo só existe depois que a base foi escolhida.
  requestAnimationFrame(() =>
    dialogRef.value?.open({ pergunta: props.question })
  );
};

const start = async () => {
  message.value = '';
  loading.value = true;
  try {
    const [ligadas, todas] = await Promise.all([
      powerDataAPI.agentStores(accountId.value, props.agentSlug),
      powerDataAPI.index(accountId.value),
    ]);
    const ids = ligadas?.power_data_ids || [];
    const lista = Array.isArray(todas) ? todas : todas?.power_data || [];
    bases.value = ids.map(id => ({
      id,
      name: lista.find(base => base.id === id)?.name || String(id),
    }));
    if (!bases.value.length) {
      message.value = t('ATENDIMENTO_REPORTS.AI.NO_BASE');
    } else if (bases.value.length === 1) {
      openFor(bases.value[0].id);
    }
  } catch {
    message.value = t('ATENDIMENTO_REPORTS.AI.BASE_FAILED');
  } finally {
    loading.value = false;
  }
};

const choosing = computed(() => (bases.value?.length || 0) > 1);
</script>

<template>
  <div class="flex flex-col gap-1 items-end">
    <Button
      data-test-id="atendimento-adicionar-a-base"
      :label="t('ATENDIMENTO_REPORTS.AI.ADD_TO_BASE')"
      xs
      faded
      type="button"
      :is-loading="loading"
      @click="start"
    />
    <div v-if="choosing" class="flex flex-wrap gap-1 justify-end">
      <Button
        v-for="base in bases"
        :key="base.id"
        :label="base.name"
        xs
        slate
        link
        type="button"
        @click="openFor(base.id)"
      />
    </div>
    <span v-if="message" class="text-xs text-n-slate-10">{{ message }}</span>
    <AddSourceDialog
      v-if="storeId"
      ref="dialogRef"
      :store-id="storeId"
      @added="bases = null"
    />
  </div>
</template>
